
import { z } from 'zod';

// Define the custom markup tier schema
export const markupTierSchema = z.object({
  id: z.string().optional(),
  line_item_id: z.string().optional(), // Add line_item_id to the schema
  min_amount: z.number().min(0, 'Minimum amount must be non-negative'),
  max_amount: z.number().nullable().optional(),
  markup_percentage: z.number().min(0, 'Markup percentage must be non-negative'),
  margin_percentage: z.number().min(0, 'Margin percentage must be non-negative'),
});

export type MarkupTier = z.infer<typeof markupTierSchema>;

// Define the main form schema
export const lineItemFormSchema = z.object({
  // Basic information
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  
  // Unit and cost fields
  base_unit: z.string().min(1, 'Base unit is required'),
  base_cost: z.number().min(0, 'Base cost must be non-negative'),
  
  // Conversion fields
  conversion_unit: z.string().optional().nullable(),
  conversion_factor: z.number().min(0).optional().nullable(),
  
  // Productivity fields
  productivity_unit: z.string().optional().nullable(),
  productivity_rate: z.number().min(0).optional().nullable(),
  
  // Tags and images
  tags: z.array(z.string()).optional().default([]),
  image_urls: z.array(z.string()).optional().default([]),
  
  // Status
  is_active: z.boolean().default(true),
  
  // Category and trade
  category: z.string().optional().nullable(),
  trade: z.string().optional().nullable(),
  category_id: z.string().optional().nullable(),
  
  // Markup and margin controls
  override_markup: z.boolean().default(false),
  custom_markup: z.number().min(0).optional().default(0),
  custom_margin: z.number().min(0).optional().default(0),
  category_markup: z.number().min(0).optional().default(0),
  category_margin: z.number().min(0).optional().default(0),
  
  // Custom markup tiers
  custom_tiers: z.array(markupTierSchema).optional().default([]),
  use_custom_tiers: z.boolean().default(false),
  
  // Pricing metadata (new fields)
  final_price: z.number().optional(),
  markup_percentage: z.number().optional(),
  markup_source: z.string().optional(),
  last_price_calculation: z.string().optional(),
  
  // Pricing source tracking
  pricing_source: z.enum(['custom', 'category', 'global']).default('global'),
  
  // Hidden ID field for edits
  id: z.string().optional(),
});

export type LineItemFormValues = z.infer<typeof lineItemFormSchema>;

// Default form values
export const defaultFormValues: Partial<LineItemFormValues> = {
  title: '',
  description: '',
  base_unit: 'hr',
  base_cost: 0,
  conversion_unit: null,
  conversion_factor: null,
  productivity_unit: null,
  productivity_rate: null,
  tags: [],
  image_urls: [],
  is_active: true,
  category: null,
  trade: null,
  category_id: null,
  override_markup: false,
  custom_markup: 0,
  custom_margin: 0,
  category_markup: 0,
  category_margin: 0,
  custom_tiers: [],
  use_custom_tiers: false,
  pricing_source: 'global',
};
